import { useEffect, useMemo, useRef, useState } from 'react';

import { useUtilityContext } from '@change-corgi/core-react/utilityContext';

import { useCookieWallDisplayed } from '#src/app/app/cookieWall';
import { usePetitionDetailsPageData, usePetitionDetailsPageUserData } from '#src/app/pages/petitionDetails/pageContext';
import type { PetitionDetailsStickySignCtaExperiment } from '#src/app/pages/petitionDetails/pageContext';
import { rendersSignCta } from '#src/app/pages/petitionDetails/shared/utilities';
import { treatExperiment } from '#src/app/shared/api/experiments';
import { useTrackExperimentExposure } from '#src/app/shared/hooks/tracking';
import { useIsPhone } from '#src/app/shared/hooks/userAgent';

import { useUnauthenticatedSignForm } from '../signForm/useUnauthenticatedSignForm';

type Params = {
	/**
	 * Whether the page is rendering the video-first hero. Passed in rather than derived here: it
	 * depends on `useVideoFirstHeroStatus(videoFirstSourceUrl)`, and the source URL only comes from
	 * `useVideoFirstExperiment()`, which this hook must not call a second time.
	 */
	videoFirstHeroLayout: boolean;
};

/**
 * Treats the sticky sign CTA experiment and fires its `$exposure`, for viewers who can actually see
 * the treatment.
 *
 * Treatment happens here rather than in the normalizer because this is the first place `eligible` is
 * computable — most of it depends on client-side page state. Treating server-side would write an
 * `experiment_variations` row for every phone viewer, including the ones who never see a sign CTA,
 * diluting the measured lift in both arms. `useTreatSignFormAddressRemovalExperiment` is the
 * precedent for this split.
 *
 * Eligibility: a phone viewer who actually gets a sign CTA (`rendersSignCta`, the shared predicate
 * `MobileActionButtonContainer` is pinned to), excluding video-first-hero pages, which already dock
 * the bar from page load and so have no effect to measure, and excluding viewers currently behind the
 * GDPR cookie wall, which covers the docked bar entirely.
 *
 * Returns the full experiment object rather than a bare variation string so callers can keep using
 * it for tracking, and so an out-of-scope viewer is indistinguishable from a real control.
 */
export function useStickySignCtaExperiment({ videoFirstHeroLayout }: Params): PetitionDetailsStickySignCtaExperiment {
	const utilityContext = useUtilityContext();
	const {
		data: {
			experiments: { petitionDetailsStickySignCtaExperiment: experiment },
			decisionMaker,
			signed,
		},
	} = usePetitionDetailsPageUserData();
	const {
		data: {
			petition: { id: petitionId, status },
		},
	} = usePetitionDetailsPageData();
	const {
		data: { enabled: unauthenticatedSignFormEnabled },
	} = useUnauthenticatedSignForm();
	// `useIsPhone`, not `useIsMobile`: the action-button tree is hidden by `block md:hidden`, and a
	// tablet typically sits at or above that breakpoint. Mirrors the server-side `isPhone` pre-filter
	// in the normalizer — see `isPhone` for why device type is an approximation of the breakpoint.
	const isPhone = useIsPhone();
	/**
	 * The consent wall is `fixed bottom-0 … z-4990` and opaque, against the docked bar's `z-29` at the
	 * same anchor — on a phone it is roughly 400px tall to the bar's 180px, so it covers it completely.
	 * A docked-bar arm therefore has no reachable sign CTA until the wall is dismissed, while control
	 * keeps its in-flow CTA unobstructed. Bucketing those viewers would read as a treatment loss for a
	 * reason unrelated to the hypothesis, and it lands one-sidedly on first-time GDPR-country visitors.
	 *
	 * Gated here rather than excluded in Experiment Service targeting because "is the wall on screen
	 * right now" depends on client-side cookie-pref state the platform can't target — the same reason
	 * the video-first-hero check lives here. It flips to false on accept/reject, so these viewers are
	 * bucketed from that point on and the bar docks mid-session rather than at first paint.
	 */
	const cookieWallDisplayed = useCookieWallDisplayed();

	const eligible =
		isPhone &&
		!videoFirstHeroLayout &&
		!cookieWallDisplayed &&
		rendersSignCta({ decisionMaker, signed, status, unauthenticatedSignFormEnabled });
	const isRealAssignment = !experiment.fallback && !experiment.override;

	// Holds the petition rather than a boolean: `treated` flips a tick later, and SPA navigation reuses
	// this hook without remounting, so keying on the petition lets the next one treat — and stops the
	// previous petition's success from being read as this one's.
	const [treatedPetitionId, setTreatedPetitionId] = useState<string | null>(null);
	const treatAttemptedForRef = useRef<string | null>(null);

	useEffect(() => {
		if (!eligible || !isRealAssignment || experiment.treated) return;
		if (treatAttemptedForRef.current === petitionId) return;
		treatAttemptedForRef.current = petitionId;

		void (async () => {
			try {
				// `treatExperiment` resolves with whether treatment was actually registered server-side, not
				// merely whether the mutation round-tripped — a bot session or a not-yet-treatable assignment
				// resolves cleanly with `treated: false`. Only flip on a real registration, or we'd report an
				// `$exposure` for a viewer with no `experiment_variations` row.
				const registered = await treatExperiment(utilityContext, experiment.experimentName);
				if (registered) setTreatedPetitionId(petitionId);
			} catch (e: unknown) {
				// Backstop if treatExperiment's resolve-never-reject contract ever regresses — this must not
				// be silent, since it would drop $exposure for the whole experiment.
				void utilityContext.errorReporter.report({
					error:
						e instanceof Error ? e : new Error('useStickySignCtaExperiment: unexpected rejection from treatExperiment'),
					params: { experimentName: experiment.experimentName },
				});
			}
		})();
	}, [eligible, isRealAssignment, experiment.treated, experiment.experimentName, petitionId, utilityContext]);

	const resolvedExperiment = useMemo<PetitionDetailsStickySignCtaExperiment>(() => {
		// Forcing `treated: false` rather than skipping the tracking hook entirely: `useTrackExperimentExposure`
		// already no-ops on `treated: false` for experiment-service experiments, and hooks can't be called
		// conditionally. This mirrors what the normalizer does for its own out-of-scope cases.
		if (!eligible) return { ...experiment, variation: 'control', treated: false };

		// `override` is included because `useTrackExperimentExposure` returns on `!treated` before it
		// reaches its own `override` branch, and a `treat: false` field resolves an override with
		// `treated: false` on a fresh session — without this a QA `?exo.` visit gets no "exposure would
		// have fired" log. No real exposure leaks: that branch logs and returns before sending.
		const nowTreated = treatedPetitionId === petitionId || experiment.treated || Boolean(experiment.override);

		return nowTreated ? { ...experiment, treated: true } : experiment;
	}, [eligible, experiment, treatedPetitionId, petitionId]);

	// `resetKeys` is required, not optional: variations are deterministic per user, so without the
	// petition id an SPA petition-to-petition navigation would dedup the new petition's legitimate
	// exposure. Same requirement as the sibling exposure hooks on this page.
	const trackExperimentExposure = useTrackExperimentExposure(resolvedExperiment, { resetKeys: [petitionId] });

	useEffect(() => {
		if (!resolvedExperiment.treated) return;
		void trackExperimentExposure();
	}, [resolvedExperiment.treated, trackExperimentExposure]);

	return resolvedExperiment;
}
